import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Tracks which route-guides the current user has already dismissed. Triggers
 * the centralised FirstVisitModal on first visit to any route with a guide.
 *
 * Keys are the `guide.match` string (e.g. "/owner", "/customer/").
 *
 * `globallyMuted` is the "don't show me these again" flag — applies to every
 * route. Reset by the Help button in the top bar.
 */
export interface FirstVisitState {
  seen: Record<string, boolean>;
  globallyMuted: boolean;
  /** Manual override — when set, the modal renders for this key even if seen. */
  forcedKey: string | null;
  markSeen: (key: string) => void;
  hasSeen: (key: string) => boolean;
  muteAll: () => void;
  unmuteAll: () => void;
  resetAll: () => void;
  /** Force-open the modal for a specific guide (e.g. via Help button). */
  force: (key: string | null) => void;
}

export const useFirstVisitStore = create<FirstVisitState>()(
  persist(
    (set, get) => ({
      seen: {},
      globallyMuted: false,
      forcedKey: null,
      markSeen: (key) =>
        set((s) => ({ seen: { ...s.seen, [key]: true }, forcedKey: null })),
      hasSeen: (key) => Boolean(get().seen[key]),
      muteAll: () => set({ globallyMuted: true, forcedKey: null }),
      unmuteAll: () => set({ globallyMuted: false }),
      resetAll: () => set({ seen: {}, globallyMuted: false, forcedKey: null }),
      force: (key) => set({ forcedKey: key }),
    }),
    {
      name: 'ati-first-visit',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
